import { useBrowserProfiles } from "../../hooks/useBrowserProfiles";
import { useConnectedDevices } from "../../hooks/useConnectedDevices";
import { usePushNotifications } from "../../hooks/usePushNotifications";
import { useI18n } from "../../i18n";
import { useSettingsPaneTitle } from "./SettingsPaneTitleContext";
import { parseUserAgent } from "../../lib/deviceDetection";

/**
 * Format a date for display with relative time.
 */
function formatDate(
  isoDate: string,
  t: (key: never, vars?: Record<string, string | number>) => string,
): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) {
    return t("devicesJustNow" as never);
  }
  if (diffMinutes < 60) {
    return t("devicesMinutesAgo" as never, {
      count: diffMinutes,
      suffix: diffMinutes === 1 ? "" : "s",
    });
  }
  if (diffHours < 24) {
    return t("devicesHoursAgo" as never, {
      count: diffHours,
      suffix: diffHours === 1 ? "" : "s",
    });
  }
  if (diffDays === 1) {
    return t("devicesYesterday" as never);
  }
  if (diffDays < 7) {
    return `${diffDays} days ago`;
  }
  return date.toLocaleDateString();
}

/**
 * Format an origin URL for display.
 * Shows a simplified version with just scheme://hostname:port
 */
function formatOrigin(origin: string): string {
  return origin;
}

/**
 * Devices settings page.
 * Shows all browser profiles with their connection origin history.
 */
export function DevicesSettings() {
  const { t } = useI18n();
  useSettingsPaneTitle(t("devicesProfilesTitle"));
  const { profiles, isLoading, error, deleteProfile } = useBrowserProfiles();
  const { browserProfileId: currentBrowserProfileId } = usePushNotifications();
  const { connections } = useConnectedDevices();

  return (
    <section className="settings-section">
      <p className="settings-section-description">
        {t("devicesProfilesDescription")}
      </p>

      {error && <p className="form-error">{error}</p>}

      <div className="settings-group">
        {isLoading ? (
          <p className="settings-hint">{t("devicesLoadingProfiles")}</p>
        ) : profiles.length === 0 ? (
          <p className="settings-hint">{t("devicesEmpty")}</p>
        ) : (
          <div className="device-list">
            {profiles.map((profile) => {
              const isCurrentDevice =
                profile.browserProfileId === currentBrowserProfileId;
              const isConnected = connections.has(profile.browserProfileId);
              const displayName =
                profile.deviceName ||
                `${profile.browserProfileId.slice(0, 8)}...`;

              return (
                <div
                  key={profile.browserProfileId}
                  className="device-list-item device-profile-item"
                >
                  <div className="device-list-info">
                    <strong>
                      <span
                        className={`device-status ${isConnected ? "device-status-online" : "device-status-offline"}`}
                        title={
                          isConnected
                            ? t("devicesConnected")
                            : t("devicesDisconnected")
                        }
                      />
                      {displayName}
                      {isCurrentDevice && (
                        <span className="device-current-badge">
                          {t("devicesThisDevice")}
                        </span>
                      )}
                    </strong>

                    {/* Origin list */}
                    <div className="device-origins">
                      {profile.origins.map((origin) => {
                        const { browser, os } = parseUserAgent(
                          origin.userAgent,
                        );
                        return (
                          <div key={origin.origin} className="device-origin">
                            <code className="device-origin-url">
                              {formatOrigin(origin.origin)}
                            </code>
                            <span className="device-origin-details">
                              {browser} · {os}
                            </span>
                            <span className="device-origin-time">
                              {t("devicesLastSeen", {
                                date: formatDate(origin.lastSeen, t),
                              })}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    <p className="device-profile-meta">
                      {t("devicesFirstSeen", {
                        date: formatDate(profile.createdAt, t),
                      })}
                    </p>
                  </div>

                  <button
                    type="button"
                    className="settings-button settings-button-danger-subtle"
                    onClick={() => deleteProfile(profile.browserProfileId)}
                    title={t("devicesForgetThisDevice")}
                  >
                    {t("devicesForget")}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
